import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ProfileLayout } from "../components/ProfileLayout"
import { Calendar, FileText, CreditCard, Clock, MapPin, Phone } from "lucide-react"

export default function PortalDashboard() {
  const navigate = useNavigate()
  const [counts, setCounts] = useState({ lab: 0, prescription: 0 })
  const [user, setUser] = useState(null)

  useEffect(() => {
    const userStr = localStorage.getItem("user")
    if (userStr) {
      const userData = JSON.parse(userStr)
      setUser(userData)
      
      let mobile = userData?.mobile || userData?.phone || ""
      if (mobile) {
        if (!mobile.startsWith("91")) {
          mobile = `91${mobile}`
        }
        
        fetch(`/apis/web/prescriptionandlabcount?mobile=${mobile}`)
          .then(res => res.json())
          .then(data => {
            if (data.status === 0 && data.result?.[0]) {
              setCounts({
                lab: data.result[0].LAB_REPORT_COUNT || 0,
                prescription: data.result[0].PRESCRIPTION_COUNT || 0
              })
            }
          })
          .catch(err => console.error("Error fetching counts:", err))
      }
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  const quickActions = [
    { name: "My Appointments", icon: Clock, href: "/my-bookings", color: "bg-green-500" },
    { name: "Lab Reports", icon: FileText, href: "/my-reports", color: "bg-purple-500" },
    { name: "Prescriptions", icon: FileText, href: "/prescriptions", color: "bg-orange-500" },
    { name: "Bills & Payments", icon: CreditCard, href: "/bills-receipts", color: "bg-red-500" },
  ]

  const todaysAppointments = [
    {
      id: 1,
      patientName: "Rajesh Kumar",
      doctor: "Dr. Sarah Wilson",
      specialty: "Cardiology",
      time: "10:30 AM",
      location: "Block A, 2nd Floor",
      status: "Confirmed",
      token: "C-15",
      wait: "25 mins"
    },
    {
      id: 2,
      patientName: "Rajesh Kumar",
      doctor: "Dr. Priya Sharma",
      specialty: "Dermatology",
      time: "2:00 PM",
      location: "Block C, Ground Floor",
      status: "Pending",
      token: "O-08",
      wait: "45 mins"
    }
  ]

  const upcomingAppointments = [
    {
      id: 1,
      doctor: "Dr. Sarah Wilson",
      specialty: "Cardiology",
      date: "2024-01-20",
      time: "10:30 AM",
      location: "Block A, 2nd Floor"
    },
    {
      id: 2,
      doctor: "Dr. Michael Chen",
      specialty: "Orthopedics",
      date: "2024-01-22",
      time: "3:00 PM",
      location: "Block B, 1st Floor"
    }
  ]

  const recentReports = [
    {
      id: 1,
      name: "Blood Test - Complete Blood Count",
      date: "2024-01-18",
      status: "Available",
      type: "Lab Report"
    },
    {
      id: 2,
      name: "X-Ray - Chest",
      date: "2024-01-15",
      status: "Available",
      type: "Radiology"
    },
    {
      id: 3,
      name: "ECG Report",
      date: "2024-01-12",
      status: "Available",
      type: "Cardiology"
    }
  ]

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Portal Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back, {user?.name || "User"}</p>
          </div>

          <div className="px-4 md:px-8">

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action) => (
                <button
                  key={action.name}
                  onClick={() => navigate(action.href)}
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  <div className={`w-12 h-12 ${action.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <action.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 text-center">{action.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Today's Appointments */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Today's Appointments</h2>
              <button onClick={() => navigate("/my-bookings")} className="text-blue-600 text-sm font-medium hover:underline">
                View All
              </button>
            </div>
            {todaysAppointments.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {todaysAppointments.map((appointment) => (
                  <div key={appointment.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{appointment.patientName}</h3>
                        <p className="text-sm text-gray-600">{appointment.doctor} - {appointment.specialty}</p>
                        <p className="text-xs text-gray-500">Token: {appointment.token} • Wait: {appointment.wait}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        appointment.status === "Confirmed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {appointment.status}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{appointment.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{appointment.location}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No appointments scheduled for today</p>
              </div>
            )}
          </div>

          {/* Upcoming Appointments */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Upcoming Appointments</h2>
              <button onClick={() => navigate("/my-bookings")} className="text-blue-600 text-sm font-medium hover:underline">
                View All
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {upcomingAppointments.map((appointment) => (
                <div key={appointment.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{appointment.doctor}</h3>
                      <p className="text-sm text-gray-600">{appointment.specialty}</p>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      Confirmed
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{appointment.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{appointment.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{appointment.location}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div> */}

          {/* Recent Reports & Results */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Recent Reports & Results</h2>
              <button onClick={() => navigate("/my-reports")} className="text-blue-600 text-sm font-medium hover:underline">
                View All
              </button>
            </div>
            <div className="mt-4">
              {recentReports.map((report, index) => (
                <div key={report.id} className={`p-4 flex items-center justify-between ${index !== recentReports.length - 1 ? 'border-b' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{report.name}</h3>
                      <p className="text-sm text-gray-600">{report.type} • {report.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      {report.status}
                    </span>
                    <button className="text-blue-600 text-sm font-medium hover:underline">
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Health Summary */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{counts.lab}</p>
                  <p className="text-sm text-gray-600">Lab Reports</p>
                </div>
              </div>
              <button onClick={() => navigate("/my-reports")} className="text-blue-600 text-sm font-medium hover:underline">
                View Reports
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{counts.prescription}</p>
                  <p className="text-sm text-gray-600">Prescriptions</p>
                </div>
              </div>
              <button onClick={() => navigate("/prescriptions")} className="text-purple-600 text-sm font-medium hover:underline">
                View Prescriptions
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">24/7</p>
                  <p className="text-sm text-gray-600">Emergency</p>
                </div>
              </div>
              <a href="tel:+919966060000" className="text-red-600 text-sm font-medium hover:underline">
                +91 99660 60000
              </a>
            </div>
          </div>
          </div>
        </div>
      </div>
    </ProfileLayout>
  )
}
